import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  VERTICAL_OPTIONS,
  type VerticalOption,
  type VerticalIcpDefaults,
} from "@/lib/verticals";

type DbRow = {
  slug: string;
  label: string;
  description: string | null;
  icp_defaults: Partial<VerticalIcpDefaults> | null;
  sort_order: number;
  is_active: boolean;
};

/**
 * Returns the active list of verticals from the DB, falling back to the
 * built-in `VERTICAL_OPTIONS` until the query resolves (or if the query
 * returns nothing).
 */
export function useVerticalSeeds() {
  const q = useQuery({
    queryKey: ["vertical-seeds-active"],
    queryFn: async (): Promise<VerticalOption[]> => {
      const { data, error } = await supabase
        .from("vertical_seeds")
        .select("slug, label, description, icp_defaults, sort_order, is_active")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data as DbRow[]).map(toVerticalOption);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const verticals = q.data && q.data.length > 0 ? q.data : VERTICAL_OPTIONS;
  return { verticals, isLoading: q.isLoading, error: q.error };
}

function toVerticalOption(row: DbRow): VerticalOption {
  const d = row.icp_defaults ?? {};
  return {
    slug: row.slug,
    label: row.label,
    description: row.description ?? undefined,
    icpDefaults: {
      titles: Array.isArray(d.titles) ? d.titles : [],
      industries: Array.isArray(d.industries) ? d.industries : [],
      company_sizes: Array.isArray(d.company_sizes) ? d.company_sizes : [],
      regions: Array.isArray(d.regions) ? d.regions : [],
      pain_points: Array.isArray(d.pain_points) ? d.pain_points : [],
    },
  };
}
